"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.List = exports.ListIsActiveContext = exports.ItemValueAttr = exports.ItemIndexAttr = void 0;
var tslib_1 = require("tslib");
var React = tslib_1.__importStar(require("react"));
var TypeStyle = tslib_1.__importStar(require("typestyle"));
var BrowserDetection_1 = require("../../utils/BrowserDetection");
var DomUtil_1 = require("../../utils/DomUtil");
var FindIndex_1 = require("../../utils/FindIndex");
var ReactHelpers_1 = require("../../utils/ReactHelpers");
var ListItemUtils_1 = require("./ListItemUtils");
var ListStyles_1 = require("./ListStyles");
exports.ItemIndexAttr = 'data-itemindex';
exports.ItemValueAttr = 'data-itemvalue';
exports.ListIsActiveContext = React.createContext(false);
exports.ListIsActiveContext.displayName = 'ListIsActiveContext';
function getDefaultActiveItemIndex(selectedValues, items) {
    if (!selectedValues) {
        return ListItemUtils_1.findFocusableItemIndex(items, -1, 1);
    }
    else if (typeof selectedValues === 'string') {
        return ListItemUtils_1.findIndexOfItemWithValue(items, selectedValues);
    }
    else {
        return items.findIndex(function (item) { return ListItemUtils_1.isFocusableItem(item) && selectedValues.has(item.value); });
    }
}
function isItemChecked(selectedValues, item) {
    if (!selectedValues) {
        return undefined;
    }
    else if (typeof selectedValues === 'string') {
        return (ListItemUtils_1.isFocusableItem(item) && item.value === selectedValues) || undefined;
    }
    else {
        return ListItemUtils_1.isFocusableItem(item) && selectedValues.has(item.value);
    }
}
/**
 * `List` displays a keyboard-navigable list of items similar to an HTML `<select size!=1>`.
 * Items are supplied via an array of `ListItem`, which has properties similar to `<option>`.
 *
 * `List` supports typeahead-focus as described in the WAI-ARIA `listbox` spec.
 * It displays a checkmark next to selected item(s), and displays a background color on the "focused" item
 * -- although only the listbox element itself is actually focusable.
 */
var List = /** @class */ (function (_super) {
    tslib_1.__extends(List, _super);
    function List() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.defaultListId = DomUtil_1.generateId();
        _this.listId = function () { return _this.props.id || _this.defaultListId; };
        _this.listElement = null;
        _this.itemTextContent = [];
        _this.typeAheadQuery = '';
        _this.typeAheadTimeoutHandle = 0;
        _this.scrollOnFocus = true;
        _this.state = {
            activeItemIndex: Math.max(0, getDefaultActiveItemIndex(_this.props.selectedValues || _this.props.selectedValue, _this.props.items)),
        };
        _this.handleFocus = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (e.target === e.currentTarget && _this.scrollOnFocus) {
                _this.scrollItemIndexIntoView(_this.state.activeItemIndex);
            }
            _this.scrollOnFocus = true;
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onFocus) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleMouseDown = ReactHelpers_1.mergeEventHandlers(function (e) {
            var activeItemIndex = getTargetedItemIndex(e.target);
            _this.scrollOnFocus = activeItemIndex > -1;
            if (activeItemIndex > -1 && activeItemIndex !== _this.state.activeItemIndex) {
                _this.setState({ activeItemIndex: activeItemIndex });
            }
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onMouseDown) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleClick = ReactHelpers_1.mergeEventHandlers(function (e) {
            var _a, _b;
            var item = _this.props.items[getTargetedItemIndex(e.target)];
            if (ListItemUtils_1.isSelectableItem(item)) {
                (_b = (_a = _this.props).onSelect) === null || _b === void 0 ? void 0 : _b.call(_a, item.value);
            }
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.keyDownHandlers = Object.freeze({
            'ArrowUp': function () { return ListItemUtils_1.findFocusableItemIndex(_this.props.items, _this.state.activeItemIndex, -1); },
            'ArrowDown': function () { return ListItemUtils_1.findFocusableItemIndex(_this.props.items, _this.state.activeItemIndex, +1); },
            'Home': function () { return ListItemUtils_1.findFocusableItemIndex(_this.props.items, -1, 1); },
            'End': function () { return ListItemUtils_1.findFocusableItemIndex(_this.props.items, _this.props.items.length, -1); },
            ' ': function (e) {
                var _a, _b;
                var activeItem = _this.props.items[_this.state.activeItemIndex];
                if (!e.repeat && ListItemUtils_1.isSelectableItem(activeItem)) {
                    (_b = (_a = _this.props).onSelect) === null || _b === void 0 ? void 0 : _b.call(_a, activeItem.value);
                }
            },
            'Enter': function (e) {
                if (_this.props.selectOnEnterKey) {
                    _this.keyDownHandlers[' '](e);
                }
            },
            'Ctrl+a': function () {
                var _a, _b;
                if (_this.props.selectedValues) {
                    (_b = (_a = _this.props).onSelectAll) === null || _b === void 0 ? void 0 : _b.call(_a);
                }
            },
        });
        _this.handleKeyDown = ReactHelpers_1.mergeEventHandlers(function (e) {
            var key = (BrowserDetection_1.isCtrl(e) ? 'Ctrl+' : '') + e.key;
            if (_this.keyDownHandlers[key]) {
                e.stopPropagation();
                e.preventDefault();
                var activeItemIndex = _this.keyDownHandlers[key](e);
                if (activeItemIndex !== undefined && activeItemIndex !== _this.state.activeItemIndex) {
                    _this.setState({ activeItemIndex: activeItemIndex });
                }
            }
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleKeyPress = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (e.key === ' ' || e.key === 'Enter' || e.altKey || e.ctrlKey || e.metaKey) {
                return;
            }
            window.clearTimeout(_this.typeAheadTimeoutHandle);
            _this.typeAheadTimeoutHandle = window.setTimeout(_this.resetQuery, 500);
            _this.typeAheadQuery += e.key;
            var query = _this.typeAheadQuery.toLowerCase();
            var activeItemIndex = FindIndex_1.findIndex(_this.itemTextContent, function (text, itemIndex) {
                return ListItemUtils_1.isFocusableItem(_this.props.items[itemIndex]) && text.toLowerCase().indexOf(query) === 0;
            }, _this.state.activeItemIndex + (query.length === 1 ? 1 : 0));
            if (activeItemIndex >= 0) {
                _this.setState({ activeItemIndex: activeItemIndex });
            }
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onKeyPress) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleListRef = function (ref) {
            _this.listElement = ref;
        };
        _this.resetQuery = function () {
            _this.typeAheadQuery = '';
        };
        return _this;
    }
    /** Gets the index into `props.items` of the pseudo-focused listbox option. */
    List.prototype.getActiveItemIndex = function () {
        return this.state.activeItemIndex;
    };
    /**
     * Sets the index into `props.items` of the pseudo-focused listbox option.
     * @param activeItemIndex the index into `props.items` of the corresponding option to focus, or `-1` for none
     */
    List.prototype.setActiveItemIndex = function (activeItemIndex) {
        if (activeItemIndex === -1 || ListItemUtils_1.isFocusableItem(this.props.items[activeItemIndex])) {
            this.setState({ activeItemIndex: activeItemIndex });
        }
    };
    List.prototype.scrollItemIndexIntoView = function (itemIndex) {
        DomUtil_1.scrollIntoView(document.getElementById(listItemId(this.listId(), itemIndex)), 'nearest');
    };
    List.prototype.componentDidMount = function () {
        this.memoizeItemTextContent();
    };
    List.prototype.componentDidUpdate = function (prevProps, prevState) {
        var items = this.props.items;
        if (!items) {
            return;
        }
        var activeItemIndex = this.state.activeItemIndex;
        if (prevProps.items !== items) {
            this.memoizeItemTextContent();
        }
        if (items.length && activeItemIndex >= items.length) {
            this.setState({ activeItemIndex: items.length - 1 });
            return;
        }
        if (prevState.activeItemIndex !== activeItemIndex) {
            this.scrollItemIndexIntoView(activeItemIndex);
        }
    };
    List.prototype.memoizeItemTextContent = function () {
        var _a;
        var elementsRenderedFromItems = (_a = this.listElement) === null || _a === void 0 ? void 0 : _a.querySelectorAll("[role=\"option\"], [role=\"group\"], [role=\"separator\"]");
        this.itemTextContent = Array.from(elementsRenderedFromItems || []).map(function (option) { return option.textContent || ''; });
    };
    List.prototype.renderItems = function () {
        var _a = this.props, items = _a.items, selectedValue = _a.selectedValue, selectedValues = _a.selectedValues;
        var activeItemIndex = this.state.activeItemIndex;
        var listId = this.listId();
        if (!items) {
            return [];
        }
        var setsize = items.filter(ListItemUtils_1.isFocusableItem).length;
        var itemNodes = [];
        var _loop_1 = function (index, posinset) {
            var item = items[index];
            if (item.separator) {
                itemNodes.push(React.createElement("div", { "data-tb-test-id": item.testId, key: "separator%%" + index, role: 'separator' }));
            }
            else {
                if (!item.value) {
                    throw new Error("Item has no value: " + JSON.stringify(item));
                }
                var makeItemElem = function (listItem) {
                    /**
                     * We're (ab)using `aria-selected` to indicate the "focused" item here.
                     * There are strongly differing opinions about the mixing of `aria-selected` with `aria-checked`.
                     * This pattern is used by SF Lightning Design System and it seems to be the only way to make multi-selection accessibly work with a combobox pattern.
                     * Also, historically Safari+VO will not read out changes to `aria-activedescendant` without also setting `aria-selected` on the target option.
                     */
                    var isActive = index === activeItemIndex || undefined;
                    var isChecked = isItemChecked(selectedValues || selectedValue, listItem);
                    posinset++; // increment first as aria-posinset starts counting from 1
                    return ListItemElem({ index: index, isSelected: isActive, isChecked: isChecked, item: listItem, listId: listId, posinset: posinset, setsize: setsize });
                };
                // this logic has much in common with similar logic in ListMenuWidget::buildMenuFromMenuItemInfos
                if (item.group) {
                    var groupLabelId = DomUtil_1.generateId();
                    var groupItemNodes = [];
                    for (index++; ListItemUtils_1.isFocusableItem(items[index]); index++) {
                        groupItemNodes.push(makeItemElem(items[index]));
                    }
                    index--;
                    itemNodes.push(React.createElement("div", { role: 'group', "aria-labelledby": groupLabelId, key: "group%%" + item.value },
                        React.createElement("div", { role: 'presentation', id: groupLabelId, "data-tb-test-id": item.testId, key: 'title' }, ListItemContent({ item: item })),
                        groupItemNodes));
                }
                else {
                    itemNodes.push(makeItemElem(item));
                }
            }
            out_index_1 = index;
            out_posinset_1 = posinset;
        };
        var out_index_1, out_posinset_1;
        for (var index = 0, posinset = 0; index < items.length; index++) {
            _loop_1(index, posinset);
            index = out_index_1;
            posinset = out_posinset_1;
        }
        return itemNodes;
    };
    List.prototype.render = function () {
        var _this = this;
        // eslint-disable-next-line max-len
        var _a = this.props, className = _a.className, items = _a.items, onSelect = _a.onSelect, onSelectAll = _a.onSelectAll, selectedValue = _a.selectedValue, selectedValues = _a.selectedValues, selectOnEnterKey = _a.selectOnEnterKey, testId = _a.testId, htmlAttrs = tslib_1.__rest(_a, ["className", "items", "onSelect", "onSelectAll", "selectedValue", "selectedValues", "selectOnEnterKey", "testId"]);
        var activeItemIndex = this.state.activeItemIndex;
        return React.createElement(exports.ListIsActiveContext.Consumer, null, function (listIsActive) {
            var _a;
            return React.createElement("div", tslib_1.__assign({}, ReactHelpers_1.EventUtilsOptOut, { tabIndex: 0 }, htmlAttrs, { "aria-activedescendant": activeItemIndex > -1 ? listItemId(_this.listId(), activeItemIndex) : undefined, "aria-multiselectable": !!selectedValues || undefined, className: TypeStyle.classes(ListStyles_1.ListStyleName, className, (_a = {}, _a[ListStyles_1.ListIsActiveClass] = listIsActive, _a)), "data-tb-test-id": testId, onClick: _this.handleClick, onFocus: _this.handleFocus, onKeyDown: _this.handleKeyDown, onKeyPress: _this.handleKeyPress, onMouseDown: _this.handleMouseDown, ref: _this.handleListRef, role: 'listbox' }), _this.renderItems());
        });
    };
    return List;
}(React.PureComponent));
exports.List = List;
/** Returns the list index of the clicked item, or -1 if none */
function getTargetedItemIndex(node) {
    var _a;
    var itemIndex = parseInt((_a = DomUtil_1.closest(node, '[role="option"]')) === null || _a === void 0 ? void 0 : _a.getAttribute(exports.ItemIndexAttr), 10);
    return isNaN(itemIndex) ? -1 : itemIndex;
}
function listItemId(listId, itemIndex) {
    return listId + "-" + itemIndex;
}
var ListItemElem = function (_a) {
    var _b;
    var index = _a.index, item = _a.item, isSelected = _a.isSelected, isChecked = _a.isChecked, listId = _a.listId, posinset = _a.posinset, setsize = _a.setsize;
    return React.createElement("div", tslib_1.__assign({ "aria-checked": isChecked, "aria-disabled": item.disabled, "aria-posinset": posinset, "aria-selected": isSelected, "aria-setsize": setsize }, (_b = {},
        _b[exports.ItemIndexAttr] = index,
        _b[exports.ItemValueAttr] = item.value,
        _b), { "data-tb-test-id": item.testId, key: "item%%" + item.value, id: listItemId(listId, index), role: 'option', title: item.tooltip }), ListItemContent({ item: item }));
};
var ListItemContent = function (_a) {
    var item = _a.item;
    return React.createElement("span", { className: ListStyles_1.EllipsedStyleName }, item.content || item.value);
};
//# sourceMappingURL=List.js.map